import _ from "lodash";
import {DICTIONARY} from '../../../dictionary';
import {GeneralService} from '../../../services/generalService';
import {RouteService} from '../../../services/routeService';
import {NotificationService} from '../../../services/notificationService';
import {Component, NgZone, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Subscription} from 'rxjs';
import {ChartComponent} from "ng-apexcharts";
import {LookAndFeelService} from "../../../services/lookAndFeelService";
import util from "util";
import html2pdf from 'html2pdf.js';
import {TrendsComparisonService} from '../../../services/trendsComparisonService';

@Component({
	selector: 'archive-trends-component',
	templateUrl: './archive-trends.component.html',
})
export class ArchiveTrendsComponent implements OnInit, OnDestroy {

	@ViewChild('archiveInboundEmailsChart', {static: false}) archiveInboundEmailsChart: ChartComponent;
	@ViewChild('topSendersChart', {static: false}) topSendersChart: ChartComponent;
	@ViewChild('topRecipientsChart', {static: false}) topRecipientsChart: ChartComponent;

	selfComponentClass = ArchiveTrendsComponent;
	wasInitializedAsGhost = false;
	archiveSummaryDeltas = null;
	comparisonSubs: Subscription[] = [];

	dic = DICTIONARY;
	_ = _;
	todayDate = new Date();

	dashboardPeriod = [
		this.dic.CONSTANTS.trendsPeriod.lastDay,
		this.dic.CONSTANTS.trendsPeriod.last3Days,
		this.dic.CONSTANTS.trendsPeriod.lastWeek,
		this.dic.CONSTANTS.trendsPeriod.lastMonth,
		this.dic.CONSTANTS.trendsPeriod.range
	];

	period = this.dic.CONSTANTS.trendsPeriod.last3Days;
	currentQuery;
	batchInProcess;
	emails;
	totalEmails
	trendsData;

	range = {start: new Date(Date.now() - (1000 * 60 * 60 * 24)), end: new Date()};
	showTrendsCustomPeriod: boolean;
	showComparePicker;
	showAbortLoadingEmailsLabel;
	abortLoadingFlag;
	getTrendsDataInProcess = false;
	generatePdfInProcess;

	// CC: taken from account-compromised-trends.component.ts -> usersFilterTypes
	usersFilterTypes = {all: 'all', specific: 'specific'};
	users;
	activeSpecificUsers;
	usersPopup;

	archiveInboundEmailsChartOptions;
	topSendersChartOptions;
	topRecipientsChartOptions;
	connectionsGraphData:any = {};
	connectionsGraphViews = {senders: 'top senders', recipients: 'top recipients'};
	connectionsGraphView = this.connectionsGraphViews.senders;
	connectionsAggregateByDomain = true;
	// CC: marker used to split a person into 2 nodes when they are both a sender and a recipient (invisible, stripped for display)
	connectionsRecipientMarker = '\u0000';
	// CC: node config supplied to echarts-graph (green=sender, blue=recipient domain)
	graphNodeCfg = {
		green: {
			color: '#2ecc71', label: 'Sender', symbol: 'circle', icon: '\uf007',
			sizeMultiplier: 1,
		},
		blue: {
			color: '#4d8af0', label: 'Recipient', symbol: 'circle', icon: '\uf0e0',
			sizeMultiplier: 1.3,
		},
	};

	constructor(public gs:GeneralService,
				private rs:RouteService,
				public lfs:LookAndFeelService,
				private ngZone:NgZone,
	            private ns:NotificationService,
	            public comparisonService: TrendsComparisonService) {
	}

	ngOnInit() {
		if (this.comparisonService.isGhostMode) {
			this.comparisonService.isGhostMode = false;
			this.wasInitializedAsGhost = true;
			return;
		}
		this.getTrendsData();
		this.rs.getArchiveUsers().then((users) => {
			this.users = users;
		}, (err) => {
		});
		this.comparisonSubs.push(
			this.comparisonService.comparisonDataReady$.subscribe(() => {
				this.computeArchiveSummaryDeltas();
			}),
			this.comparisonService.comparisonDeactivated$.subscribe(() => {
				this.archiveSummaryDeltas = null;
			})
		);
	}

	ngOnDestroy(): void {
		this.abortLoadingFlag = true;
		if (!this.wasInitializedAsGhost) {
			this.comparisonService.deactivateComparison();
		}
		this.comparisonSubs.forEach(s => s.unsubscribe());
	}

	computeArchiveSummaryDeltas = () => {
		const compared = this.comparisonService.ghostInstance?.trendsData?.general;
		const current = this.trendsData?.general;
		if (!compared || !current || (!compared.total && !current.total)) {
			this.archiveSummaryDeltas = null;
			return;
		}

		const calcPct = (curr, comp) => comp ? (curr - comp) / comp * 100 : (curr ? 100 : 0);
		const totalPercentage = calcPct(compared.total, current.total);
		const sendersPercentage = calcPct(compared.senders, current.senders);
		const recipientsPercentage = calcPct(compared.recipients, current.recipients);
		this.archiveSummaryDeltas = {
			total: totalPercentage,
			totalAbs: Math.abs(totalPercentage),
			senders: sendersPercentage,
			sendersAbs: Math.abs(sendersPercentage),
			recipients: recipientsPercentage,
			recipientsAbs: Math.abs(recipientsPercentage),
		};
	}

	getTrendsData = () => {
		this.currentQuery = {period: this.period.value};
		if (this.period.value === this.dic.CONSTANTS.trendsPeriod.range.value) {
			this.currentQuery.range = this.range;
		}
		this.getTrendsDataInProcess = true;
		this.trendsData = null;
		this.abortLoadingFlag = false;


		this.rs.getArchiveTrends(this.currentQuery).then((response) => {
			this.emails = response.emails || [];
			this.totalEmails = response.total;
			this.prepareData(false);
			this.getTrendsDataInProcess = false;

			if (response.scrollId) {
				this.showAbortLoadingEmailsLabel = true;
				this.batchInProcess = true;
				this.getTrendsDataBatch(response.scrollId);
			}
			else {
				this.batchInProcess = false;
				this.initTrendsProcessParams();
			}
		}, (err) => {
			this.getTrendsDataInProcess = false;
		});
	}

	getTrendsDataBatch = (scrollId) => {
		if (this.abortLoadingFlag) {
			this.abortLoadingFlag = false;
			return;
		}

		this.rs.getArchiveTrends({scrollId}).then((response) => {
			this.emails = this.emails.concat(response.emails);
			this.prepareData(true);

			if (response.scrollId) {
				this.getTrendsDataBatch(response.scrollId)
			}
			else {
				this.batchInProcess = false;
				this.showAbortLoadingEmailsLabel = false;
				this.initTrendsProcessParams();
			}
		}, (err) => {
		});
	}

	parseTrendsData(emails, currentQuery) {
		let trendsPeriod: any = this.getTrendsPeriodForGraphs(currentQuery);

		const trendsData = {
			general: {
				total: emails.length,
				senders: 0,
				recipients: 0,
				labels: [],
				data: [[]],
			},
			senders: {
				labels: [],
				data: [],
			},
			recipients: {
				labels: [],
				data: [],
			},
		};

		// @ts-ignore
		emails.sort((a, b) => new Date(a._source?.created) - new Date(b._source?.created));

		// CC: count emails per sender (from) and per recipient (rcptTo)
		const sendersCount = {};
		const recipientsCount = {};
		let emailSourcePerPeriod: any = {};

		emails.forEach((currentSource) => {
			const from = currentSource._source?.from;
			if (from) {
				sendersCount[from] = (sendersCount[from] || 0) + 1;
			}

			const rcptTo = currentSource._source?.rcptTo || [];
			rcptTo.forEach((recipient) => {
				if (recipient) {
					recipientsCount[recipient] = (recipientsCount[recipient] || 0) + 1;
				}
			});

			if (!emailSourcePerPeriod.dateStart) {
				emailSourcePerPeriod = this.initEmailSourcePerPeriod(currentSource, trendsPeriod);
			}

			if (this.gs.isSamePeriod(trendsPeriod, currentSource._source?.created, emailSourcePerPeriod.dateEnd)) {
				emailSourcePerPeriod.total += 1;
			}
			else {
				this.addArchiveGraphData(trendsData, emailSourcePerPeriod, trendsPeriod);
				emailSourcePerPeriod = this.initEmailSourcePerPeriod(currentSource, trendsPeriod);
				emailSourcePerPeriod.total += 1;
			}
		});

		this.addArchiveGraphData(trendsData, emailSourcePerPeriod, trendsPeriod);

		let sendersArray = Object.entries(sendersCount);
		trendsData.general.senders = sendersArray?.length;
		// @ts-ignore
		sendersArray.sort((a, b) => b[1] - a[1]);
		sendersArray = sendersArray.slice(0, 10);
		sendersArray.forEach(([label, count]) => {
			trendsData.senders.labels.push(label);
			trendsData.senders.data.push(count);
		});

		let recipientsArray = Object.entries(recipientsCount);
		trendsData.general.recipients = recipientsArray?.length;
		// @ts-ignore
		recipientsArray.sort((a, b) => b[1] - a[1]);
		recipientsArray = recipientsArray.slice(0, 10);
		recipientsArray.forEach(([label, count]) => {
			trendsData.recipients.labels.push(label);
			trendsData.recipients.data.push(count);
		});

		return trendsData;
	}

	getTrendsPeriodForGraphs(currentQuery) {
		let trendsPeriod = currentQuery.period;
		if (currentQuery.period === this.dic.CONSTANTS.trendsPeriod.range.value && currentQuery.range) {
			const days = this.gs.dateDiffInDays(currentQuery.range.end, currentQuery.range.start);
			//35 = 7 days * 5 weeks - limit graph to 5 weeks
			if (days >= 90) {
				trendsPeriod = this.dic.CONSTANTS.trendsPeriod.lastMonths.value;
			}
			else if (days >= 7) {
				trendsPeriod = this.dic.CONSTANTS.trendsPeriod.lastWeek.value;
			}
			else {
				trendsPeriod = this.dic.CONSTANTS.trendsPeriod.lastDay.value;
			}
		}
		return trendsPeriod;
	}

	initEmailSourcePerPeriod(currentSource, period) {
		const emailSourcePerPeriod = this.gs.calcDatesForPeriod({created: currentSource._source?.created}, period);
		emailSourcePerPeriod.total = 0;

		return emailSourcePerPeriod;
	}

	addArchiveGraphData(trendsData, emailSourcePerPeriod, period) {
		if (emailSourcePerPeriod.dateStart) {
			const dateLabel = this.gs.getPeriodLabel(period, emailSourcePerPeriod);
			trendsData.general.labels.push(dateLabel);
			trendsData.general.data[0].push(emailSourcePerPeriod.total);
		}
	}


	initTrendsProcessParams = () => {};

	openComparePicker = () => {
		if (!this.showComparePicker && !this.comparisonService.comparisonActive) {
			this.comparisonService.setDefaultDates(this.comparisonService.granularity);
		}
		this.showComparePicker = !this.showComparePicker;
	}

	prepareData = (isUpdate) => {
		// CC: filter emails client-side when specific users are selected
		let emailsToProcess = this.emails;
		if (this.activeSpecificUsers?.length) {
			const userEmailsSet = new Set(_.map(this.activeSpecificUsers, 'email'));
			emailsToProcess = this.emails.filter(e =>
				userEmailsSet.has(e._source?.from) ||
				(e._source?.rcptTo || []).some(r => userEmailsSet.has(r))
			);
		}

		this.trendsData = this.parseTrendsData(emailsToProcess, this.currentQuery);

		this.prepareArchiveInboundEmailsGraph(isUpdate);
		this.prepareTopSendersGraph(isUpdate);
		this.prepareTopRecipientsGraph(isUpdate);
		this.prepareConnectionsGraph();
	};

	// CC: taken from outbound-trends.component.ts -> prepareConnectionsGraph
	prepareConnectionsGraph = () => {
		// CC: set of everyone who appears as a sender (used to split a person into 2 nodes if they're also a recipient)
		const allSenders = new Set();
		(this.emails || []).forEach((currentSource) => {
			const from = currentSource._source?.from;
			if (from) {
				allSenders.add(from);
			}
		});

		const bySenders = this.connectionsGraphView === this.connectionsGraphViews.senders;
		const top5Senders = new Set(this.trendsData.senders.labels.slice(0, 5));
		const top5Recipients = new Set(this.trendsData.recipients.labels.slice(0, 5));

		// CC: build links - top 5 senders -> their recipients, or their senders -> top 5 recipients
		const links = [];
		(this.emails || []).forEach((currentSource) => {
			const from = currentSource._source?.from;
			if (!from || (bySenders && !top5Senders.has(from))) {
				return;
			}

			const rcptTo = currentSource._source?.rcptTo || [];
			rcptTo.forEach((recipient) => {
				if (!recipient || (!bySenders && !top5Recipients.has(recipient))) {
					return;
				}

				let source = from;
				let target = recipient;

				if (this.connectionsAggregateByDomain) {
					// CC: aggregate the "many" side by domain to reduce the number of nodes
					if (bySenders) {
						target = recipient.split('@')[1] || recipient;
					}
					else {
						source = from.split('@')[1] || from;
					}
				}
				else if (allSenders.has(recipient)) {
					// CC: if a recipient is also a sender, mark it so it renders as a separate node
					target = recipient + this.connectionsRecipientMarker;
				}

				links.push({source, target});
			});
		});

		this.connectionsGraphData = {
			graphData: {links},
			nodeOptionsFn: this.connectionsNodeOptionsFn,
			nodeSearchTxt: '',
			nodeSearchResults: null,
		};
	};

	// CC: shared node styling for the connections graph
	connectionsNodeOptionsFn = (node, allLinks) => {
		const sentToCounter = _.filter(allLinks, { target: node.name }).length;
		const isUserNode = _.map(allLinks, 'source').includes(node.name);
		node.color = isUserNode ? 'green' : '';
		// CC: strip the recipient marker so the node displays the clean email
		const displayName = node.name.split(this.connectionsRecipientMarker)[0];
		node.name = sentToCounter && !isUserNode ? displayName + ` (${sentToCounter})` : displayName;
		node.lineWidth = sentToCounter && !isUserNode ? Math.log(Math.min(sentToCounter, 20))*2 : 1;

		// hide the tooltip that shows numbers of links of a node (because we show another number in label)
		node.hideTooltip = true;
	};

	// CC: toggle between top senders and top recipients, then rebuild the graph
	setConnectionsGraphView = (view) => {
		if (this.connectionsGraphView === view) {
			return;
		}

		this.connectionsGraphView = view;
		this.prepareConnectionsGraph();
	};

	prepareArchiveInboundEmailsGraph = (isUpdate) => {
		const series = [{
			name: 'Emails',
			data: this.trendsData.general.data[0]
		}];

		if (isUpdate && this.archiveInboundEmailsChartOptions?.series?.length &&
			this.gs.areArraysEqual(this.trendsData.general.labels, this.archiveInboundEmailsChartOptions.xaxis.categories)) {
			this.archiveInboundEmailsChart.updateSeries(series, true);
			return;
		}

		this.archiveInboundEmailsChartOptions = {
			series: series,
			chart: {
				type: this.trendsData.general.labels?.length === 1 ? "bar" : "area",
				height: '100%',
				toolbar: {
					show: false
				},
			},

			colors: ['#1a4d99'],
			dataLabels: {
				enabled: false
			},
			stroke: {
				show: true,
				width: 4,
				colors: ['transparent']
			},
			xaxis: {
				tickAmount: 4, // Set the maximum number of ticks (labels) to be displayed on the x-axis
				categories: this.trendsData.general.labels,
			},
			yaxis: {
				title: {
					text: 'Emails'
				}
			},
			fill: {
				opacity: 1
			},
			tooltip: {
				y: {
					formatter: (val) => {
						return val + ' emails';
					}
				}
			}
		};
	};

	prepareTopSendersGraph = (isUpdate) => {
		const series = [{
			name: 'Emails',
			data: this.trendsData.senders.data
		}];

		if (isUpdate && this.topSendersChartOptions?.xaxis?.categories?.length &&
			this.gs.areArraysEqual(this.trendsData.senders.labels, this.topSendersChartOptions.xaxis.categories)) {
			this.topSendersChart.updateSeries(series, true);
			return;
		}

		this.topSendersChartOptions = {
			defaultPolarity: 'neutral',
			series: series,
			chart: {
				type: 'bar',
				height: '100%',
				toolbar: {
					show: false
				},
				animations: {
					dynamicAnimation: {
						enabled: false
					}
				}
			},
			colors: ['#a54343'],

			plotOptions: {
				bar: {
					borderRadius: 4,
					horizontal: true,
					barHeight: this.gs.getOptimalBarWidth(this.trendsData.senders.labels.length) + '%',
				}
			},
			dataLabels: {
				total: {
					enabled: true,
					offsetX: 0,
					style: {
						fontSize: '13px',
						fontWeight: 900
					}
				}
			},
			xaxis: {
				categories: this.trendsData.senders.labels,
			},
		};
	};

	prepareTopRecipientsGraph = (isUpdate) => {
		const series = [{
			name: 'Emails',
			data: this.trendsData.recipients.data
		}];

		if (isUpdate && this.topRecipientsChartOptions?.xaxis?.categories?.length &&
			this.gs.areArraysEqual(this.trendsData.recipients.labels, this.topRecipientsChartOptions.xaxis.categories)) {
			this.topRecipientsChart.updateSeries(series, true);
			return;
		}

		this.topRecipientsChartOptions = {
			defaultPolarity: 'neutral',
			series: series,
			chart: {
				type: 'bar',
				height: '100%',
				toolbar: {
					show: false
				},
				animations: {
					dynamicAnimation: {
						enabled: false
					}
				}
			},
			colors: ['#a54343'],
			plotOptions: {
				bar: {
					borderRadius: 4,
					horizontal: true,
					barHeight: this.gs.getOptimalBarWidth(this.trendsData.recipients.labels.length) + '%',
				}
			},
			dataLabels: {
				total: {
					enabled: true,
					offsetX: 0,
					style: {
						fontSize: '13px',
						fontWeight: 900
					}
				}
			},
			xaxis: {
				categories: this.trendsData.recipients.labels,
			},
		};
		// CC: force a redraw after options are assigned — dataLabels don't visually appear otherwise
		// until a browser resize triggers ApexCharts' own resize handler. updateOptions({}) replicates that.
		setTimeout(() => {
			this.topRecipientsChart?.updateOptions({}, false, false);
		}, 100);
	};



	// CC: taken from account-compromised-trends.component.ts -> openUsersFilterPopup
	openUsersFilterPopup = () => {
		this.usersPopup = {
			type: this.activeSpecificUsers ? this.usersFilterTypes.specific : this.usersFilterTypes.all,
			users: this.activeSpecificUsers ? _.unionBy(this.activeSpecificUsers, _.cloneDeep(this.users), 'email') : _.cloneDeep(this.users),
			show: true
		};
	}

	// CC: taken from account-compromised-trends.component.ts -> applyUsersFilter
	applyUsersFilter = () => {
		if (this.usersPopup.type === this.usersFilterTypes.specific && !_.find(this.usersPopup.users, 'selected')) {
			this.ns.showErrorMessage('You must select at least one user');
			return;
		}
		if (this.usersPopup.type === this.usersFilterTypes.specific) {
			this.activeSpecificUsers = _.filter(this.usersPopup.users, 'selected');
		}
		else {
			this.activeSpecificUsers = null;
		}
		this.usersPopup = null;
		if (this.comparisonService.comparisonActive) {
			this.comparisonService.activateComparison();
		}
		else if (this.emails) {
			this.prepareData(false);
		}
		else {
			this.getTrendsData();
		}
	}

	// CC: taken from account-compromised-trends.component.ts -> searchUser
	searchUser = (searchTerm) => {
		searchTerm = searchTerm.toLowerCase();
		this.usersPopup.users.forEach(record => {
			if (searchTerm) {
				const isFound = (record.email.toLowerCase().indexOf(searchTerm) > -1);
				if (!isFound) {
					record.hide = true;
					return;
				}
			}
			record.hide = false;
		});
	}

	changePeriod = (period) => {
		if (this.comparisonService.comparisonActive) {
			this.comparisonService.deactivateComparison();
		}
		if (period.value === this.dic.CONSTANTS.trendsPeriod.range.value) {
			if (this.range.start.getTime() > Date.now() || this.range.start > this.range.end) {
				this.ns.showWarnMessage(this.dic.ERRORS.adminTrendsDate);
				return;
			}
		}

		this.period = period;
		this.getTrendsData();
	}

	exportChartsToPdf = () => {
		if (!this.trendsData) {
			return;
		}
		this.generatePdfInProcess = true;

		// set the relevant elements' visual properties to be ideal for the PDF page before copying the HTML
		let areaForPdf:any = document.getElementById('trendsChartsContainer');
		areaForPdf.classList.add('trends-pdf-layout');

		const isComparison = this.comparisonService.comparisonActive;
		const canvasWidth = isComparison ? 1700 : 1300;
		if (isComparison) {
			areaForPdf.style.setProperty('--chart-pdf-layout-width', `${canvasWidth}px`);
		}

		// timeout is needed to let the css enough time to update on screen and for the responsive charts to resize
		setTimeout(() => {
			const element = document.getElementById('trendsChartsContainer');
			const opt = {
				margin:       40,
				filename:     'trends_and_insights.pdf',
				image:        { type: 'jpeg' as 'jpeg', quality: 1 },
				html2canvas:  { scale: 2 , width: canvasWidth, windowWidth: canvasWidth + 250},
				jsPDF:        { unit: 'px', format: 'a4', orientation: 'portrait' as 'portrait', hotfixes: ['px_scaling']}
			};

			// New Promise-based usage:
			html2pdf().set(opt).from(element).save().finally(() => {
				setTimeout(() => {
					this.exitFromPdfGeneration();
				})
			});
		},2000);
	}

	exitFromPdfGeneration = () => {
		this.ns.showInfoMessage(util.format(this.dic.MESSAGES.downloadSuccess, 'Trends and insights data'));

		const container = document.getElementById('trendsChartsContainer');
		container.classList.remove('trends-pdf-layout');
		container.style.removeProperty('--chart-pdf-layout-width');
		this.generatePdfInProcess = false;
		// rerender charts so they'll fit their container's size
		this.getTrendsDataInProcess = true;

		setTimeout(() => {
			this.ngZone.run(() => {
				/*				const gElemement:any = document.getElementById('trendsConnectionsGraph');
								this.activeConnectionGraph.width(gElemement.clientWidth);
								this.activeConnectionGraph.height(gElemement.clientHeight);
								this.activeConnectionGraph.zoomToFit();*/

				this.getTrendsDataInProcess = false;
			});
		},100);
	}

	getPercentage = (partialNumber, totalRecords) => {
		if (!totalRecords) {
			return 0;
		}
		const percentage = Math.floor(100 * partialNumber / totalRecords);
		if (percentage > 100) {
			return 100;
		}
		return percentage;
	}

	abortLoadingEmails = () => {
		this.abortLoadingFlag = true;
		this.showAbortLoadingEmailsLabel = false;
		this.getTrendsDataInProcess = false;
		this.batchInProcess = false;
	}
}
